import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Sparkles, FileText, AlertTriangle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import type { CalendarEventTemplateDetail } from '@/lib/api';
import type { CalendarEventInput, EventInstance, TimeFormat } from '../types';
import { formatTime, findConflicts } from '../utils';

interface QuickEventDialogProps {
  open: boolean;
  date: Date;
  hour: number;
  onClose: () => void;
  onSave: (title: string) => Promise<void>;
  onExpand: (data?: Partial<CalendarEventInput>) => void;
  templates?: CalendarEventTemplateDetail[];
  timeFormat?: TimeFormat;
  defaultEventDuration?: number;
  existingEvents?: EventInstance[];
  showConflictWarnings?: boolean;
  allowDoubleBooking?: boolean;
}

export function QuickEventDialog({
  open,
  date,
  hour,
  onClose,
  onSave,
  onExpand,
  templates,
  timeFormat = '12h',
  defaultEventDuration = 60,
  existingEvents = [],
  showConflictWarnings = true,
  allowDoubleBooking = true,
}: QuickEventDialogProps) {
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  const startTime = new Date(date);
  startTime.setHours(hour, 0, 0, 0);
  const endTime = new Date(startTime.getTime() + defaultEventDuration * 60000);

  // Conflict detection for the quick event time slot
  const conflicts = useMemo(() => {
    if (!showConflictWarnings) return [];
    return findConflicts(startTime.toISOString(), endTime.toISOString(), existingEvents);
  }, [showConflictWarnings, startTime, endTime, existingEvents]);

  const saveBlocked = !allowDoubleBooking && conflicts.length > 0;

  // Close template menu on outside click
  useEffect(() => {
    if (!templateMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (templateRef.current && !templateRef.current.contains(e.target as Node)) {
        setTemplateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [templateMenuOpen]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ title: 'Title required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await onSave(title.trim());
      setTitle('');
      onClose();
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create event',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleParseNatural = async () => {
    if (!title.trim()) {
      toast({ title: 'Enter a description first', variant: 'destructive' });
      return;
    }
    setParsing(true);
    try {
      const parsed = await api.parseNaturalEvent({
        text: title.trim(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referenceDate: new Date().toISOString(),
      });

      const data: Partial<CalendarEventInput> = { title: parsed.title };
      if (parsed.startAt) data.startAt = parsed.startAt;
      if (parsed.endAt) data.endAt = parsed.endAt;
      if (parsed.isAllDay !== undefined) data.isAllDay = parsed.isAllDay;
      if (parsed.location) data.location = parsed.location;
      if (parsed.description) data.description = parsed.description;
      if (parsed.isRecurring !== undefined) data.isRecurring = parsed.isRecurring;
      if (parsed.rrule) data.rrule = parsed.rrule;

      setTitle('');
      onExpand(data);
    } catch {
      setTitle('');
      onExpand({ title: title.trim() });
    } finally {
      setParsing(false);
    }
  };

  const handleSelectTemplate = (t: CalendarEventTemplateDetail) => {
    setTitle('');
    setTemplateMenuOpen(false);
    const data: Partial<CalendarEventInput> = {
      title: t.title,
      description: t.description || undefined,
      location: t.location || undefined,
      isAllDay: t.isAllDay,
      status: t.status,
      color: t.color,
      url: t.url || undefined,
      isRecurring: t.isRecurring,
      rrule: t.rrule || undefined,
    };
    if (t.calendarId) data.calendarId = t.calendarId;
    if (t.durationMinutes) {
      const s = new Date(date);
      s.setHours(hour, 0, 0, 0);
      const e = new Date(s.getTime() + t.durationMinutes * 60000);
      data.startAt = s.toISOString();
      data.endAt = e.toISOString();
    }
    onExpand(data);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setTitle('');
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Quick Event</DialogTitle>
          <DialogDescription>
            {formatTime(startTime, timeFormat)} – {formatTime(endTime, timeFormat)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Event title or describe in natural language..."
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            className="flex-1"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={handleParseNatural}
            disabled={parsing || saving || !title.trim()}
            title="Parse with AI"
            aria-label="Parse with AI"
          >
            {parsing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Conflict warning */}
        {conflicts.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>
              Overlaps with {conflicts.map((c) => c.title).join(', ')}
              {saveBlocked && ' — double booking is disabled'}
            </span>
          </div>
        )}

        <DialogFooter className="gap-2">
          <div className="mr-auto flex items-center gap-1">
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setTitle('');
                onExpand();
              }}
              className="text-xs"
            >
              More options
            </Button>
            {templates && templates.length > 0 && (
              <div ref={templateRef} className="relative">
                <Button
                  variant="link"
                  size="sm"
                  className="text-xs gap-1"
                  onClick={() => setTemplateMenuOpen(!templateMenuOpen)}
                >
                  <FileText className="h-3 w-3" />
                  From template
                </Button>
                {templateMenuOpen && (
                  <div className="absolute bottom-full left-0 mb-1 z-50 w-48 rounded-md border bg-popover p-1 shadow-md">
                    {templates.map((t) => (
                      <button
                        key={t.id}
                        className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors"
                        onClick={() => handleSelectTemplate(t)}
                      >
                        <div className="font-medium truncate text-xs">{t.name}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving || parsing}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || parsing || saveBlocked}
            title={saveBlocked ? 'Resolve scheduling conflicts before saving' : undefined}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
